import * as XLSX from 'xlsx';
import {
  EQUIPMENT_DOC_TYPES,
  EQUIPMENT_FIELD_KEYS,
} from '../data/master/equipments';
import { downloadBlob, exportFileStamp } from './exportExcel';

const CUSTOM_DOC_SLOT_COUNT = 4;
const BASIC_HEADERS = [
  'name',
  'category',
  'vehicleClass',
  'description',
  'isActive',
];

function customDocCol(n) {
  return `customDoc${n}`;
}

function formatCustomDoc(entry) {
  if (!entry || !entry.label) return '';
  const level = entry.level === 'required' ? 'required' : 'optional';
  return `${entry.label}:${level}`;
}

/**
 * Download only error rows in the same EquipmentTypes template shape,
 * with a final `error` column describing why each row failed.
 */
export function downloadEquipmentTypesImportErrorRows(errorRows) {
  const rows = Array.isArray(errorRows) ? errorRows : [];
  if (!rows.length) {
    throw new Error('No error rows to download');
  }

  const fieldHeaders = (EQUIPMENT_FIELD_KEYS || []).map((f) => f.key);
  const docHeaders = [
    ...(EQUIPMENT_DOC_TYPES || []).map((d) => d.key),
    ...Array.from({ length: CUSTOM_DOC_SLOT_COUNT }, (_, i) =>
      customDocCol(i + 1),
    ),
  ];
  const colHeaders = [...BASIC_HEADERS, ...fieldHeaders, ...docHeaders, 'error'];

  const basicCount = BASIC_HEADERS.length;
  const fieldCount = fieldHeaders.length;
  const docCount = docHeaders.length;

  const groupRow = [
    'Basic Details',
    ...Array(Math.max(0, basicCount - 1)).fill(''),
    'Fields',
    ...Array(Math.max(0, fieldCount - 1)).fill(''),
    'Document',
    ...Array(Math.max(0, docCount - 1)).fill(''),
    'Errors',
  ];

  const dataRows = rows.map((row) => {
    const data = row?.data || {};
    const fr =
      data.fieldRequirements && typeof data.fieldRequirements === 'object'
        ? data.fieldRequirements
        : {};
    const dr =
      data.docRequirements && typeof data.docRequirements === 'object'
        ? data.docRequirements
        : {};
    const customDocs = Array.isArray(dr.customDocs) ? dr.customDocs : [];
    const errorText = Array.isArray(row?.errors)
      ? row.errors
          .filter((e) => e != null && String(e).trim() !== '')
          .map(String)
          .join('; ')
      : row?.errors != null && row?.errors !== ''
        ? String(row.errors)
        : '';

    return [
      data.name ?? '',
      data.category ?? '',
      data.vehicleClass ?? '',
      data.description ?? '',
      data.isActive === false ? 'No' : 'Yes',
      ...fieldHeaders.map((k) => fr[k] || ''),
      ...(EQUIPMENT_DOC_TYPES || []).map((d) => dr[d.key] || ''),
      ...Array.from({ length: CUSTOM_DOC_SLOT_COUNT }, (_, i) =>
        formatCustomDoc(customDocs[i]),
      ),
      errorText,
    ];
  });

  const aoa = [groupRow, colHeaders, ...dataRows];
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  const fieldStart = basicCount;
  const docStart = basicCount + fieldCount;
  const errorCol = basicCount + fieldCount + docCount;

  sheet['!merges'] = [
    { s: { r: 0, c: 0 }, e: { r: 0, c: basicCount - 1 } },
    {
      s: { r: 0, c: fieldStart },
      e: { r: 0, c: fieldStart + fieldCount - 1 },
    },
    {
      s: { r: 0, c: docStart },
      e: { r: 0, c: docStart + docCount - 1 },
    },
  ];
  sheet['!cols'] = colHeaders.map((h, i) => ({
    wch: i === errorCol ? 48 : Math.min(22, Math.max(12, String(h).length + 2)),
  }));

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, sheet, 'EquipmentTypes');

  const buf = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  downloadBlob(
    blob,
    `equipment_types_import_errors_${exportFileStamp()}.xlsx`,
  );
}
